import React, { useState, useRef, useEffect } from 'react';
import { X } from 'lucide-react';
import { supabase } from '../lib/supabase';
import toast from 'react-hot-toast';
import FormInput from './FormInput';
import LoadingButton from './LoadingButton';

interface RequestModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const RequestModal: React.FC<RequestModalProps> = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    type: '',
    description: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({
    type: '',
    description: '',
  });

  const modalRef = useRef<HTMLDivElement>(null);
  const initialFocusRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) {
      initialFocusRef.current?.focus();
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  const validateForm = () => {
    const newErrors = {
      type: '',
      description: '',
    };

    if (!formData.type) {
      newErrors.type = 'Please select Prayer or Praise';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    }

    setErrors(newErrors);
    return !newErrors.type && !newErrors.description;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    
    try {
      const { error } = await supabase
        .from('requests')
        .insert([{
          name: formData.name.trim() || null,
          type: formData.type,
          description: formData.description.trim(),
        }]);

      if (error) throw error;

      toast.success(`${formData.type === 'prayer' ? 'Prayer' : 'Praise'} request submitted successfully!`);
      onClose();
      setFormData({
        name: '',
        type: '',
        description: '',
      });
      setErrors({
        type: '',
        description: '',
      });
    } catch (error) {
      console.error('Error submitting request:', error);
      toast.error('Failed to submit request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
    >
      <div 
        ref={modalRef}
        className="bg-white rounded-lg p-4 sm:p-6 md:p-8 max-w-2xl w-full mx-2 sm:mx-4 max-h-[95vh] sm:max-h-[90vh] overflow-y-auto"
        role="document"
      >
        <div className="flex justify-between items-center mb-4 sm:mb-6">
          <h2 id="modal-title" className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900">
            Submit Prayer & Praise Request
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 transition-colors p-1"
            aria-label="Close modal"
          >
            <X className="w-5 h-5 sm:w-6 sm:h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
          <FormInput
            ref={initialFocusRef}
            label="Name (Optional)"
            type="text"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="Your name (optional)"
            maxLength={100}
          />

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2 sm:mb-3">
              Request Type *
            </label>
            <div className="space-y-2 sm:space-y-3">
              <label className="flex items-center">
                <input
                  type="radio"
                  name="type"
                  value="prayer"
                  checked={formData.type === 'prayer'}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                  className="mr-2 sm:mr-3 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm sm:text-base text-gray-700">🙏 Prayer Request</span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="type"
                  value="praise"
                  checked={formData.type === 'praise'}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                  className="mr-2 sm:mr-3 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm sm:text-base text-gray-700">🙌 Praise Report</span>
              </label>
            </div>
            {errors.type && (
              <p className="mt-1 text-sm text-red-600">{errors.type}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description *
            </label>
            <textarea
              required
              maxLength={2000}
              rows={4}
              className={`w-full px-3 py-2 sm:px-4 sm:py-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition text-sm sm:text-base ${
                errors.description ? 'border-red-500' : 'border-gray-300'
              }`}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder={
                formData.type === 'prayer' 
                  ? "Please share your prayer request. We will lift this up in prayer during our gathering."
                  : formData.type === 'praise'
                  ? "Share your praise report! We'd love to celebrate God's goodness with you."
                  : "Please select Prayer or Praise above, then share your request here."
              }
            />
            <div className="flex justify-between items-center mt-1">
              {errors.description && (
                <p className="text-sm text-red-600">{errors.description}</p>
              )}
              <p className="text-xs sm:text-sm text-gray-500 ml-auto">
                {formData.description.length}/2000 characters
              </p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-4 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="w-full sm:w-auto px-4 sm:px-6 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors text-sm sm:text-base"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <LoadingButton
              type="submit"
              loading={isSubmitting}
              className="w-full sm:w-auto px-4 sm:px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm sm:text-base"
            >
              Submit Request
            </LoadingButton>
          </div>

          <div className="bg-blue-50 rounded-lg p-3 sm:p-4 text-xs sm:text-sm text-blue-800">
            <p className="font-medium mb-1 sm:mb-2">🤝 Community Guidelines:</p>
            <p className="text-blue-700 leading-relaxed">
              Requests will be shared with the prayer team to pray over during the Prayer & Fasting event.
            </p>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RequestModal;